import classNames from 'classnames';
import { useContext } from 'react';
import * as React from 'react';
import { Icon, IconName } from 'palette/Icon';
import { Transition } from 'palette/Transition';
import {
    BodyText, Small, SmallBodyLevel, Weight, TagName,
} from 'palette/Typography';
import AccordionContext from './AccordionContext';
import './styles/Accordion.scss';

type AccordionProps = React.PropsWithChildren<{
    label: string;
    tagName?: TagName;
    level?: SmallBodyLevel;
    className?: string;
    caption?: string;
    iconName?: IconName;
    divider?: boolean;
    weight?: Weight;
    component?: React.ElementType;
    headerClassName?: string;
    contentClassName?: string;
    unmountOnHide?: boolean;
}>;

export default function Accordion({
    children,
    label,
    tagName,
    level = '1',
    className,
    caption,
    iconName,
    divider = true,
    weight = 'regular',
    component: Component,
    headerClassName = '',
    contentClassName = '',
    unmountOnHide = true
}: AccordionProps) {
    const classes = classNames('palette-accordion__header', headerClassName, {
        'palette-accordion__header-divider': divider,
    });
    const { expanded, toggleExpanded } = useContext(AccordionContext);

    return (
        <>
            <button
                aria-expanded={expanded(label)}
                className={classNames(classes, className)}
                onClick={() => toggleExpanded(label)}
            >
                {iconName && (
                    <Icon
                        className="palette-accordion__icon"
                        name={iconName}
                    />
                )}
                <div className="palette-accordion__label">
                    <BodyText
                        level={level}
                        weight={weight}
                        tagName={tagName}
                    >
                        {label}
                    </BodyText>
                    {caption && (
                        <Small color="light">{caption}</Small>
                    )}
                </div>
                {Component && <Component />}
                <div
                    className={classNames('palette-accordion__expand-icon', {
                        'palette-accordion__expand-icon--active': expanded(label),
                    })}
                >
                    <Icon
                        name="palette_chevron_down"
                        size="x-small"
                    />
                </div>
            </button>
            <Transition
                appear
                unmountOnExit={unmountOnHide}
                className={classNames('palette-accordion__content', contentClassName)}
                property="opacity, display, max-height"
                show={expanded(label)}
                timeout="quick"
            >
                {/* div container because Transition component expects to receive a single React element child */}
                <div>{children}</div>
            </Transition>
        </>
    );
}
